/**
 * 项目卡片骨架屏组件
 * 在数据加载时显示占位内容
 */
import React from 'react';
import { Card, Skeleton, Row, Col } from 'antd';

export interface ProjectSkeletonProps {
  count?: number;
  isMobile?: boolean;
  viewMode?: 'card' | 'compact' | 'table' | 'list';
}

// 紧凑卡片骨架
const CompactCardSkeleton: React.FC<{ isMobile?: boolean }> = ({ isMobile }) => (
  <Card
    size="small"
    bodyStyle={{ padding: isMobile ? '8px 12px' : '12px 16px' }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
      <Skeleton.Input active size="small" style={{ width: 100 }} />
      <Skeleton.Button active size="small" style={{ width: 60 }} />
    </div>
    <Skeleton.Input active size="small" style={{ width: '100%', marginBottom: 6 }} />
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Skeleton.Input active size="small" style={{ width: 80 }} />
      <Skeleton.Input active size="small" style={{ width: 40 }} />
    </div>
  </Card>
);

// 完整卡片骨架
const FullCardSkeleton: React.FC<{ isMobile?: boolean }> = ({ isMobile }) => (
  <Card
    size={isMobile ? 'small' : 'default'}
    bodyStyle={{ padding: isMobile ? 12 : 24 }}
  >
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <Skeleton.Input active size="small" style={{ width: 120 }} />
        <Skeleton.Button active size="small" style={{ width: 70 }} />
      </div>
      <Skeleton.Input active size="small" style={{ width: 150 }} />
    </div>
    
    <div style={{ marginBottom: 16 }}>
      <Skeleton.Input active size="small" style={{ width: '100%', marginBottom: 8 }} />
      <Skeleton.Input active size="small" style={{ width: '100%' }} />
    </div>
    
    <div style={{ marginBottom: 16 }}>
      <Skeleton.Input active size="small" style={{ width: '100%', marginBottom: 8 }} />
      <Skeleton.Input active size="small" style={{ width: '100%' }} />
    </div>
    
    <Row gutter={[8, 8]}>
      <Col span={12}>
        <Skeleton.Input active size="small" style={{ width: 60 }} />
      </Col>
      <Col span={12}>
        <Skeleton.Input active size="small" style={{ width: 80 }} />
      </Col>
    </Row>
    
    <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between' }}>
      <Skeleton.Button active size="small" style={{ width: 80 }} />
      <div>
        <Skeleton.Button active size="small" style={{ width: 50, marginRight: 8 }} />
        <Skeleton.Button active size="small" style={{ width: 70 }} />
      </div>
    </div>
  </Card>
);

// 列表项骨架
const ListItemSkeleton: React.FC = () => (
  <Card size="small" style={{ marginBottom: 8 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
      <Skeleton.Input active size="small" style={{ width: 120 }} />
      <Skeleton.Button active size="small" style={{ width: 60 }} />
    </div>
    <Skeleton.Input active size="small" style={{ width: '100%' }} />
  </Card>
);

// 表格骨架
const TableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div>
    {/* 表头 */}
    <div style={{ 
      display: 'flex', 
      padding: '12px 16px', 
      background: '#fafafa',
      borderBottom: '1px solid #f0f0f0'
    }}>
      {[150, 100, 180, 140, 90, 160, 150].map((width, index) => (
        <Skeleton.Input 
          key={index} 
          active 
          size="small" 
          style={{ width, marginRight: 16 }} 
        />
      ))}
    </div>
    {/* 表格行 */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div 
        key={rowIndex}
        style={{ 
          display: 'flex', 
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0'
        }}
      >
        {[150, 100, 180, 140, 90, 160, 150].map((width, colIndex) => (
          <Skeleton.Input 
            key={colIndex} 
            active 
            size="small" 
            style={{ width, marginRight: 16 }} 
          />
        ))}
      </div>
    ))}
  </div>
);

export const ProjectSkeleton: React.FC<ProjectSkeletonProps> = ({
  count = 6,
  isMobile = false,
  viewMode = 'card'
}) => {
  if (viewMode === 'table') {
    return <TableSkeleton rows={count} />;
  }

  if (viewMode === 'list') {
    return (
      <div>
        {Array.from({ length: count }).map((_, index) => (
          <ListItemSkeleton key={index} />
        ))}
      </div>
    );
  }

  // 卡片视图（紧凑或完整）
  const CardComponent = viewMode === 'compact' ? CompactCardSkeleton : FullCardSkeleton;
  const colConfig = viewMode === 'compact'
    ? { xs: 12, sm: 8, md: 6, lg: 6 }
    : { xs: 24, sm: 24, md: 12, lg: 8 };

  return (
    <Row gutter={[16, 16]}>
      {Array.from({ length: count }).map((_, index) => (
        <Col key={index} {...colConfig}>
          <CardComponent isMobile={isMobile} />
        </Col>
      ))}
    </Row>
  );
};

export default ProjectSkeleton;
